import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useTeamStore } from './team'

export interface User {
  id: string
  email: string
  name: string
  plan: 'free' | 'solo' | 'team' | 'scale'
  role: 'owner' | 'admin' | 'member'
  globalRole?: 'user' | 'admin' | 'superadmin'
  avatarUrl?: string
  emailVerified?: boolean
  createdAt: string
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  /** Unix timestamp (seconds) when the access token expires. */
  tokenExpiresAt: number | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  login: (user: User, token: string, refreshToken: string, tokenExpiresAt?: number) => void
  logout: () => void
  updateUser: (user: Partial<User>) => void
  setLoading: (loading: boolean) => void
  setTokens: (token: string, refreshToken: string, tokenExpiresAt?: number) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: true,

      login: (user, token, refreshToken, tokenExpiresAt) =>
        set({
          user,
          token,
          refreshToken,
          tokenExpiresAt: tokenExpiresAt ?? null,
          isAuthenticated: true,
          isLoading: false
        }),

      logout: () => {
        // Clear team context when logging out to prevent stale data
        // when a different user logs in on the same browser
        useTeamStore.getState().clearTeamContext()
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          isLoading: false
        })
      },

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null
        })),

      setLoading: (isLoading) => set({ isLoading }),

      setTokens: (token, refreshToken, tokenExpiresAt) =>
        set({ token, refreshToken, tokenExpiresAt: tokenExpiresAt ?? null }),
    }),
    {
      name: 'ghostflow-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Set loading to false after rehydration completes
        if (state) {
          state.isLoading = false
        }
      },
    }
  )
)

// ---------------------------------------------------------------------------
// Cross-tab synchronization via storage events
// ---------------------------------------------------------------------------
// When another tab updates localStorage (e.g. after refreshing tokens),
// this listener syncs the in-memory Zustand state so that the current tab
// doesn't continue using stale tokens that the backend already invalidated.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== 'ghostflow-auth' || !event.newValue) return

    try {
      const persisted = JSON.parse(event.newValue) as {
        state?: Partial<AuthState>
      }
      const incoming = persisted?.state
      if (!incoming) return

      const current = useAuthStore.getState()

      // Another tab logged out → log out here too
      if (!incoming.isAuthenticated && current.isAuthenticated) {
        useTeamStore.getState().clearTeamContext()
        useAuthStore.setState({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          isLoading: false,
        })
        return
      }

      // Another tab logged in → adopt the full auth state so that
      // persist doesn't write back a partial (isAuthenticated: false)
      // state which would trigger a false "logged out" event on the
      // originating tab.
      if (incoming.isAuthenticated && !current.isAuthenticated) {
        useAuthStore.setState({
          user: (incoming.user as User) ?? null,
          token: incoming.token ?? null,
          refreshToken: incoming.refreshToken ?? null,
          tokenExpiresAt: incoming.tokenExpiresAt ?? null,
          isAuthenticated: true,
          isLoading: false,
        })
        return
      }

      // Another tab refreshed tokens → adopt the new tokens
      if (
        incoming.token &&
        incoming.refreshToken &&
        incoming.token !== current.token
      ) {
        useAuthStore.setState({
          token: incoming.token,
          refreshToken: incoming.refreshToken,
          tokenExpiresAt: incoming.tokenExpiresAt ?? null,
        })
      }
    } catch {
      // Ignore malformed storage data
    }
  })
}
